import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ColonyDotName } from "@/lib/colonyColors";
import { Plus, RefreshCw, Trash2, CreditCard, Building2, PauseCircle } from "lucide-react";
import { toast } from "sonner";

const fmtDt = (iso) => {
  if (!iso) return "Never";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch (_) { return iso; }
};

const fmtMoney = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString(undefined, { style: "currency", currency: "USD" });
};

// Payment due within 7 days = red chip; further out = amber. Handles ISO
// date strings like "2026-03-15" safely.
const isDueSoon = (iso) => {
  if (!iso) return false;
  const due = new Date(iso + "T00:00:00").getTime();
  if (!Number.isFinite(due)) return false;
  const daysAway = (due - Date.now()) / 86400000;
  return daysAway <= 7;
};

/** Inner button that owns the Plaid Link lifecycle. Re-mounts when the token
 * changes so it never holds onto an expired link_token. */
const OAUTH_PATH = "/admin/finance";
const isOAuthReturn = () => typeof window !== "undefined" && window.location.search.includes("oauth_state_id");

// Only a requested/resumed connection owns Link. Merely visiting Cards must
// not insert and then tear down a still-loading SDK during tab navigation.
function PlaidLinkSession({ token, onSuccess, onExit }) {
  const opened = useRef(false);
  const { open, ready, error } = usePlaidLink({
    token,
    onSuccess,
    onExit,
    receivedRedirectUri: isOAuthReturn() ? window.location.href : undefined,
  });
  useEffect(() => {
    if (ready && !opened.current) {
      opened.current = true;
      open();
    }
  }, [ready, open]);
  useEffect(() => {
    if (error) onExit({ error_message: "Unable to load the bank connection. Please try again." }, {});
  }, [error, onExit]);
  return null;
}

function ConnectCardButton({ onConnected }) {
  // On an OAuth redirect back from the bank, resume Link with the token we
  // stashed before leaving (Plaid does a full-page redirect for OAuth banks).
  const [linkToken, setLinkToken] = useState(() => (isOAuthReturn() ? localStorage.getItem("plaid_link_token") : null));
  const [loading, setLoading] = useState(false);

  const requestToken = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post("/plaid/link-token");
      localStorage.setItem("plaid_link_token", r.data.link_token);
      setLinkToken(r.data.link_token);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  const clearOAuthUrl = useCallback(() => {
    if (isOAuthReturn()) window.history.replaceState({}, "", OAUTH_PATH);
  }, []);

  const onSuccess = useCallback(async (public_token, metadata) => {
    try {
      const r = await api.post("/plaid/exchange-public-token", {
        public_token,
        institution_name: metadata?.institution?.name || "",
        institution_id: metadata?.institution?.institution_id || "",
      });
      toast.success(`Connected ${r.data.institution_name} — syncing transactions…`);
      // Auto-trigger first sync so the user immediately sees expenses fill in.
      try { await api.post(`/plaid/items/${r.data.item_id}/sync`); } catch (_) { /* first-sync failure is non-fatal */ }
      localStorage.removeItem("plaid_link_token");
      setLinkToken(null);
      clearOAuthUrl();
      onConnected && onConnected();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  }, [clearOAuthUrl, onConnected]);

  const onExit = useCallback((err, metadata) => {
    localStorage.removeItem("plaid_link_token");
    setLinkToken(null);
    clearOAuthUrl();
    if (err) {
      console.error("Plaid Link exit:", err, metadata);
      const code = err.error_code || "";
      const msg = err.error_message || err.display_message || "";
      toast.error(`Bank connect stopped${code ? ` (${code})` : ""}${msg ? `: ${msg}` : ". Try again or pick your bank from the list."}`, { duration: 9000 });
    }
  }, [clearOAuthUrl]);

  return (
    <>
    <Button
      onClick={requestToken}
      disabled={loading || !!linkToken}
      className="rounded-sm w-full sm:w-auto"
      data-testid="plaid-connect-btn"
    >
      <Plus className="w-4 h-4 mr-2" />
      {loading ? "Preparing…" : "Connect a card / bank"}
    </Button>
    {linkToken && <PlaidLinkSession key={linkToken} token={linkToken} onSuccess={onSuccess} onExit={onExit} />}
    </>
  );
}


/**
 * Admin → Finance → Cards tab. Lists every Plaid Item the business has
 * connected, with per-item Sync + Disconnect, plus a global "Sync now" that
 * pulls fresh transactions for every card in one click.
 */
export default function PlaidCards() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null); // null | "all" | <item_id>
  const [plaidEnv, setPlaidEnv] = useState(null); // "sandbox" | "production"

  useEffect(() => {
    api.get("/plaid/env").then((r) => setPlaidEnv(r.data.env)).catch(() => {});
  }, []);

  const [colonies, setColonies] = useState([]);
  const [saving, setSaving] = useState(null);
  const [usage, setUsage] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const load = useCallback(async () => {
    try {
      const [itemsR, colR, useR, dupR] = await Promise.all([
        api.get("/plaid/items"),
        api.get("/mgmt/colonies").catch(() => ({ data: [] })),
        api.get("/plaid/usage").catch(() => ({ data: null })),
        api.get("/plaid/duplicates").catch(() => ({ data: [] })),
      ]);
      setItems(itemsR.data);
      setColonies(colR.data || []);
      setUsage(useR.data);
      setDuplicates(dupR.data || []);
    } catch (e) {
      if (e.response?.status !== 503) {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
      }
      setItems([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolveDup = async (reviewId, action) => {
    try {
      await api.post(`/plaid/duplicates/${reviewId}/resolve`, { action });
      toast.success(action === "keep_manual" ? "Kept the manual entry — Plaid tx discarded" : action === "keep_plaid" ? "Replaced with the Plaid tx" : "Both kept (side by side)");
      setDuplicates((prev) => prev.filter((d) => d.id !== reviewId));
      // Invalidate the cached history so the next "Show history" click
      // shows the row that was just resolved.
      setHistory([]);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const loadHistory = async () => {
    // Cached — a fresh fetch only fires when the panel opens or after
    // a resolve/reopen that would move a row across the pending/history line.
    setHistoryLoading(true);
    try {
      const r = await api.get("/plaid/duplicates/history?limit=100");
      setHistory(r.data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const reopenDup = async (reviewId) => {
    try {
      await api.post(`/plaid/duplicates/${reviewId}/reopen`);
      toast.success("Re-opened — back in the pending review list");
      // Refetch both lists so pending vs history stay in sync
      const [dupR, histR] = await Promise.all([
        api.get("/plaid/duplicates").catch(() => ({ data: [] })),
        api.get("/plaid/duplicates/history?limit=100").catch(() => ({ data: [] })),
      ]);
      setDuplicates(dupR.data || []);
      setHistory(histR.data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history.length === 0) loadHistory();
  };

  const assignAccountColony = async (accountId, colonyId) => {
    setSaving(accountId);
    try {
      await api.patch(`/plaid/accounts/${accountId}`, { colony_id: colonyId || null });
      toast.success(colonyId ? "Card assigned to colony" : "Colony assignment cleared");
      // Optimistically update local state so the dropdown reflects the
      // new value without waiting for a full reload.
      setItems((prev) => prev.map((it) => ({
        ...it,
        accounts: (it.accounts || []).map((a) => a.account_id === accountId ? { ...a, colony_id: colonyId || null } : a),
      })));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(null); }
  };

  const toggleEnabled = async (item) => {
    // Local flip first so the switch feels instant; revert on error.
    const next = !(item.enabled === false ? false : true);
    setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, enabled: next } : it));
    try {
      await api.patch(`/plaid/items/${item.id}/enabled`, { enabled: next });
      toast.success(next ? "Card resumed — syncing will run again" : "Card paused — no more sync charges");
    } catch (e) {
      // Revert on failure
      setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, enabled: !next } : it));
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const toggleAccountEnabled = async (account) => {
    const next = !(account.enabled === false ? false : true);
    setItems((prev) => prev.map((it) => ({
      ...it,
      accounts: (it.accounts || []).map((a) => a.account_id === account.account_id ? { ...a, enabled: next } : a),
    })));
    try {
      await api.patch(`/plaid/accounts/${account.account_id}/enabled`, { enabled: next });
      toast.success(next ? "Card resumed" : "Card paused — this account only");
    } catch (e) {
      setItems((prev) => prev.map((it) => ({
        ...it,
        accounts: (it.accounts || []).map((a) => a.account_id === account.account_id ? { ...a, enabled: !next } : a),
      })));
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const syncAll = async () => {
    setBusy("all");
    try {
      const r = await api.post("/plaid/sync-all");
      const totals = r.data.reduce((acc, s) => ({
        added: acc.added + (s.added || 0),
        modified: acc.modified + (s.modified || 0),
        errors: acc.errors + (s.status === "error" ? 1 : 0),
      }), { added: 0, modified: 0, errors: 0 });
      toast.success(`Synced ${items.length} card${items.length === 1 ? "" : "s"} — ${totals.added} new, ${totals.modified} updated${totals.errors ? `, ${totals.errors} failed` : ""}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  const refreshBalances = async () => {
    setBusy("balances");
    try {
      const r = await api.post("/plaid/refresh-balances");
      const ok = (r.data || []).filter((x) => x.status === "ok").length;
      const err = (r.data || []).filter((x) => x.status === "error").length;
      toast.success(`Balances refreshed on ${ok} card${ok === 1 ? "" : "s"}${err ? `, ${err} failed` : ""}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  const syncOne = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/plaid/items/${id}/sync`);
      if (r.data.status === "ok") toast.success(`Synced — ${r.data.added} new, ${r.data.modified} updated`);
      else toast.error(`Sync failed: ${r.data.error || "unknown"}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  const syncOneAccount = async (accountId) => {
    setBusy(accountId);
    try {
      const r = await api.post(`/plaid/accounts/${accountId}/sync`);
      if (r.data.status === "ok") toast.success(`Synced just this card — ${r.data.added} new, ${r.data.modified} updated`);
      else toast.error(`Sync failed: ${r.data.error || "unknown"}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  const [backfillMonths, setBackfillMonths] = useState({});
  const backfillOneAccount = async (accountId) => {
    const months = backfillMonths[accountId] || 1;
    setBusy(`backfill-${accountId}`);
    try {
      const r = await api.post(`/plaid/accounts/${accountId}/backfill?months=${months}`);
      toast.success(
        `Pulled ${months} month${months > 1 ? "s" : ""} — checked ${r.data.checked}, ${r.data.added} new, ${r.data.review} sent to review`
      );
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  const disconnect = async (item) => {
    if (!window.confirm(`Disconnect ${item.institution_name}? Imported expenses stay in the books, but no new transactions will be pulled.`)) return;
    setBusy(item.id);
    try {
      await api.delete(`/plaid/items/${item.id}`);
      toast.success("Card disconnected");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="plaid-cards-tab">
      <div className="border border-border bg-secondary/20 rounded-sm p-3 sm:p-5">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">Connect a credit card or bank</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Securely link Chase, American Express, Capital One (or any major US bank). Transactions auto-import as expenses with categories already mapped.
              You never share your bank password with us — login happens on the bank&apos;s own site through Plaid.
            </p>
            {plaidEnv === "sandbox" && (
              <p className="text-[10px] text-muted-foreground font-mono mt-2">
                SANDBOX MODE — use test login <span className="text-foreground">user_good</span> / <span className="text-foreground">pass_good</span>.
                Once you&apos;ve verified the flow works, we&apos;ll switch to live keys.
              </p>
            )}
            {plaidEnv === "production" && (
              <p className="text-[10px] text-emerald-600 font-mono mt-2 inline-flex items-center gap-1">
                LIVE MODE — connect your real bank. Use your actual online-banking login.
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:flex sm:flex-wrap gap-2 items-center">
          <ConnectCardButton onConnected={load} />
          {items.length > 0 && (
            <Button
              variant="outline"
              onClick={syncAll}
              disabled={busy !== null}
              className="rounded-sm w-full sm:w-auto"
              data-testid="plaid-sync-all-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${busy === "all" ? "animate-spin" : ""}`} />
              {busy === "all" ? "Syncing…" : "Sync now"}
            </Button>
          )}
          {items.length > 0 && (
            <Button
              variant="outline"
              onClick={refreshBalances}
              disabled={busy !== null}
              className="rounded-sm w-full sm:w-auto"
              title="Fetch fresh balance + due-date only — no transaction pull"
              data-testid="plaid-refresh-balances-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${busy === "balances" ? "animate-spin" : ""}`} />
              {busy === "balances" ? "Refreshing…" : "Refresh balances"}
            </Button>
          )}
          {/* Sync-cost meter — shows API-call count this month + when the
              last call happened, so the admin can budget the ~$0.30/call
              production cost. */}
          {usage && (
            <span
              className="inline-flex flex-wrap items-center gap-1.5 min-h-11 text-[11px] text-muted-foreground bg-secondary/50 border border-border rounded-sm px-2 py-1"
              title={`${usage.this_month_count} Plaid API call${usage.this_month_count === 1 ? "" : "s"} this month${usage.last_call_at ? ` — last on ${fmtDt(usage.last_call_at)} (${usage.last_endpoint || "unknown"})` : ""}`}
              data-testid="plaid-usage-badge"
            >
              <span className="font-semibold text-foreground">{usage.this_month_count}</span>
              call{usage.this_month_count === 1 ? "" : "s"} this month
              {usage.last_call_at && <span className="text-muted-foreground">· last {fmtDt(usage.last_call_at)}</span>}
            </span>
          )}
        </div>
      </div>

      {duplicates.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-sm overflow-hidden" data-testid="plaid-duplicates-panel">
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-amber-900 bg-amber-100 border-b border-amber-300 flex items-center justify-between">
            <span>⚠ Possible duplicate charges — review before adding ({duplicates.length})</span>
          </div>
          <ul className="divide-y divide-amber-200">
            {duplicates.map((d) => {
              const m = d.existing_expense || {};
              const p = d.plaid_tx_doc || {};
              return (
                <li key={d.id} className="p-4" data-testid={`plaid-dup-${d.id}`}>
                  <div className="text-xs uppercase tracking-wider text-amber-800 font-semibold mb-2 flex flex-wrap items-center gap-2">
                    <span>{m.date || p.date} · card ••{d.card_mask} · {fmtMoney(m.amount ?? p.amount)}</span>
                    {d.match_type === "fuzzy" && (
                      <span
                        className="text-[10px] normal-case tracking-normal font-semibold bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-sm"
                        title={d.match_reason || "Amounts don't match exactly — likely a tip or tax difference"}
                        data-testid={`plaid-dup-fuzzy-${d.id}`}
                      >
                        Fuzzy match{d.match_reason ? ` · ${d.match_reason}` : ""}
                      </span>
                    )}
                    {d.match_type === "exact" && (
                      <span className="text-[10px] normal-case tracking-normal font-semibold bg-emerald-200 text-emerald-900 px-1.5 py-0.5 rounded-sm">
                        Exact match
                      </span>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="border border-border rounded-sm bg-background p-3">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Already entered by you</div>
                      <div className="font-semibold">{m.vendor || m.category || "Expense"}</div>
                      <div className="text-sm text-muted-foreground mt-1">{m.notes || "—"}</div>
                      <div className="text-[11px] mt-2">Submitted by <span className="font-semibold">{m.submitter_name || "—"}</span></div>
                      <div className="text-[11px]">Category: {m.category || "—"}</div>
                      {m.amount != null && d.match_type === "fuzzy" && (
                        <div className="text-[11px] mt-1">Amount: <span className="font-mono">{fmtMoney(m.amount)}</span></div>
                      )}
                    </div>
                    <div className="border border-border rounded-sm bg-background p-3">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Just pulled from the card</div>
                      <div className="font-semibold">{p.vendor || p.category || "Card charge"}</div>
                      <div className="text-sm text-muted-foreground mt-1">{p.notes || "—"}</div>
                      <div className="text-[11px] mt-2">Auto category: {p.category || "—"}</div>
                      {p.amount != null && d.match_type === "fuzzy" && (
                        <div className="text-[11px] mt-1">Amount: <span className="font-mono">{fmtMoney(p.amount)}</span></div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
                    <Button size="sm" onClick={() => resolveDup(d.id, "keep_manual")} className="rounded-sm" data-testid={`plaid-dup-keep-manual-${d.id}`}>Keep mine — discard card row</Button>
                    <Button size="sm" variant="outline" onClick={() => resolveDup(d.id, "keep_plaid")} className="rounded-sm" data-testid={`plaid-dup-keep-plaid-${d.id}`}>Replace with card row</Button>
                    <Button size="sm" variant="ghost" onClick={() => resolveDup(d.id, "keep_both")} className="rounded-sm" data-testid={`plaid-dup-keep-both-${d.id}`}>Keep both</Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Duplicate History — always mounted (even when no pending),
          so admins can revisit past decisions and re-open one if they
          clicked the wrong action. Collapsed by default; the row list
          is only fetched on first expand. */}
      <div className="border border-border rounded-sm overflow-hidden" data-testid="plaid-duplicates-history-panel">
        <button
          type="button"
          onClick={toggleHistory}
          className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-secondary/40 border-b border-border hover:bg-secondary/60"
          data-testid="plaid-duplicates-history-toggle"
        >
          <span className="flex items-center gap-2">
            <RefreshCw className="w-3.5 h-3.5" />
            Duplicate history {history.length > 0 && <span className="text-[10px] text-muted-foreground">({history.length})</span>}
          </span>
          <span className="text-[10px]">{historyOpen ? "hide" : "show"}</span>
        </button>
        {historyOpen && (
          <div>
            {historyLoading ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</p>
            ) : history.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">No resolved duplicates yet. Once you pick an action on a pending review it will show up here.</p>
            ) : (
              <ul className="divide-y divide-border">
                {history.map((d) => {
                  const m = d.existing_expense || {};
                  const p = d.plaid_tx || {};
                  const label =
                    d.status === "keep_manual" ? "Kept manual · card row discarded"
                    : d.status === "keep_plaid" ? "Replaced with card row"
                    : d.status === "keep_both" ? "Kept both"
                    : d.status;
                  return (
                    <li key={d.id} className="p-4 text-sm" data-testid={`plaid-hist-${d.id}`}>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs uppercase tracking-wider font-semibold text-emerald-800">
                          {label}
                        </span>
                        {d.match_type === "fuzzy" && (
                          <span className="text-[10px] normal-case tracking-normal font-semibold bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-sm" title={d.match_reason || ""}>
                            Fuzzy match
                          </span>
                        )}
                        <span className="text-[11px] text-muted-foreground ml-auto">
                          {d.resolved_at ? new Date(d.resolved_at).toLocaleString() : ""} · {d.resolver_name}
                        </span>
                      </div>
                      <div className="text-[12px] text-muted-foreground mb-2">
                        {p.date || m.date || ""} · card ••{d.card_mask} · {fmtMoney(m.amount ?? p.amount)}
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="border border-border rounded-sm bg-background p-2">
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Manual entry</div>
                          <div className="truncate">{m.vendor || m.category || m.notes || (d.status === "keep_plaid" ? "(deleted on replace)" : "—")}</div>
                        </div>
                        <div className="border border-border rounded-sm bg-background p-2">
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Card charge</div>
                          <div className="truncate">{p.vendor || p.category || p.notes || "—"}</div>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => reopenDup(d.id)}
                          className="rounded-sm h-7 text-xs"
                          data-testid={`plaid-hist-reopen-${d.id}`}
                          title="Re-open this review back to the pending list"
                        >
                          Re-open
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>


      <div className="border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-secondary/40 border-b border-border">
          Connected accounts ({items.length})
        </div>
        {items.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">No cards or banks connected yet. Click <strong>Connect a card / bank</strong> above to get started.</p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <li key={item.id} className={`px-3 sm:px-4 py-3 ${item.enabled === false ? "opacity-60" : ""}`} data-testid={`plaid-item-${item.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <Building2 className="w-5 h-5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-2">
                      {item.institution_name || "Connected card"}
                      {item.enabled === false && (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm font-semibold inline-flex items-center gap-1">
                          <PauseCircle className="w-3 h-3" /> Paused
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      Connected {fmtDt(item.created_at)} · Last synced {fmtDt(item.last_synced_at)}
                    </div>
                  </div>
                  {/* On/off — pausing prevents any sync (no per-sync charge). */}
                  <div className="flex items-center gap-1.5 ml-auto sm:ml-0" title={item.enabled === false ? "Turn on to resume syncing" : "Turn off to pause syncing (no per-sync charges while off)"}>
                    <Switch
                      checked={item.enabled !== false}
                      onCheckedChange={() => toggleEnabled(item)}
                      data-testid={`plaid-enabled-${item.id}`}
                    />
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => syncOne(item.id)}
                    disabled={busy !== null || item.enabled === false}
                    className="rounded-sm"
                    data-testid={`plaid-sync-${item.id}`}
                    title={item.enabled === false ? "Card is paused" : "Sync this card now"}
                  >
                    <RefreshCw className={`w-4 h-4 ${busy === item.id ? "animate-spin" : ""}`} />
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => disconnect(item)}
                    disabled={busy !== null}
                    className="rounded-sm text-destructive hover:text-destructive"
                    data-testid={`plaid-disconnect-${item.id}`}
                    title="Disconnect"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {/* Per-account rows: each individual card on this connection
                    gets its own colony assignment. Multiple Chase cards can
                    each go to different bungalow colonies. */}
                {(item.accounts || []).length > 0 && (
                  <div className="mt-3 sm:ml-8 space-y-3" data-testid={`plaid-accounts-${item.id}`}>
                    {item.accounts.map((a) => {
                      const acctPaused = a.enabled === false;
                      return (
                      <div key={a.account_id} className={`flex items-center gap-2 sm:gap-3 text-sm flex-wrap border-t border-border/60 pt-3 first:border-t-0 first:pt-0 ${acctPaused ? "opacity-60" : ""}`} data-testid={`plaid-account-${a.account_id}`}>
                        {/* Individual card on/off — independent of the item switch. */}
                        <Switch
                          checked={!acctPaused}
                          onCheckedChange={() => toggleAccountEnabled(a)}
                          data-testid={`plaid-account-enabled-${a.account_id}`}
                          title={acctPaused ? "Turn on to include this card in sync" : "Turn off to pause syncing for this card only"}
                        />
                        <span className="flex-1 min-w-0 truncate" title={a.official_name || a.name}>
                          {a.name || "Card"}{a.mask ? ` ••${a.mask}` : ""}
                          {a.subtype && <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-1.5">{a.subtype}</span>}
                          {acctPaused && (
                            <span className="ml-1.5 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm font-semibold inline-flex items-center gap-1">
                              <PauseCircle className="w-3 h-3" /> Paused
                            </span>
                          )}
                        </span>
                        {a.balance_current != null && (
                          <span
                            className="text-[10px] font-mono uppercase tracking-wider bg-secondary text-foreground px-1.5 py-0.5 rounded-sm"
                            title={a.balance_limit ? `Available ${fmtMoney(a.balance_limit - (a.balance_current || 0))} of ${fmtMoney(a.balance_limit)}` : "Current balance"}
                            data-testid={`plaid-balance-${a.account_id}`}
                          >
                            Bal {fmtMoney(a.balance_current)}
                          </span>
                        )}
                        {a.last_statement_balance != null && (
                          <span
                            className="text-[10px] font-mono uppercase tracking-wider bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded-sm"
                            title={a.last_statement_issue_date ? `Statement issued ${a.last_statement_issue_date}` : "Last statement balance"}
                            data-testid={`plaid-statement-${a.account_id}`}
                          >
                            Stmt {fmtMoney(a.last_statement_balance)}
                          </span>
                        )}
                        {a.next_payment_due_date && (
                          <span
                            className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${isDueSoon(a.next_payment_due_date) ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}
                            title={a.minimum_payment_amount != null ? `Minimum payment ${fmtMoney(a.minimum_payment_amount)}` : "Next payment due"}
                            data-testid={`plaid-due-${a.account_id}`}
                          >
                            Due {a.next_payment_due_date}{a.minimum_payment_amount != null ? ` · min ${fmtMoney(a.minimum_payment_amount)}` : ""}
                          </span>
                        )}
                        {/* Colored colony dropdown — dot next to each option
                            matches the colony's assigned color for at-a-glance
                            matching with the Bungalow colonies table. */}
                        <Select
                          value={a.colony_id || "__none__"}
                          disabled={saving === a.account_id}
                          onValueChange={(v) => assignAccountColony(a.account_id, v === "__none__" ? "" : v)}
                        >
                          <SelectTrigger
                            className="h-11 sm:h-8 rounded-sm w-full sm:w-[240px] max-w-full"
                            data-testid={`plaid-account-colony-${a.account_id}`}
                          >
                            <SelectValue placeholder="— No colony (general) —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">
                              <span className="text-muted-foreground">— No colony (general) —</span>
                            </SelectItem>
                            {colonies.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                <ColonyDotName id={c.id} name={c.name} />
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {/* Per-card manual sync — pulls fresh charges for
                            just this card so the admin isn't forced to
                            sync the whole bank (and pay per-item cost)
                            just to refresh one card. */}
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => syncOneAccount(a.account_id)}
                          disabled={busy !== null || acctPaused || item.enabled === false}
                          className="rounded-sm h-8 px-2"
                          data-testid={`plaid-account-sync-${a.account_id}`}
                          title={acctPaused ? "Card is paused" : item.enabled === false ? "Bank connection is paused" : "Sync just this card now (advances cursor for the whole bank — sibling cards' transactions on this pull are dropped)"}
                        >
                          <RefreshCw className={`w-4 h-4 ${busy === a.account_id ? "animate-spin" : ""}`} />
                        </Button>
                        {/* Date-range backfill, independent of Plaid's cursor —
                            recovers older statement charges the incremental
                            sync already passed by and can never re-offer. */}
                        <div className="flex items-center gap-1 ml-auto sm:ml-0">
                          <Select
                            value={String(backfillMonths[a.account_id] || 1)}
                            onValueChange={(v) => setBackfillMonths((prev) => ({ ...prev, [a.account_id]: Number(v) }))}
                          >
                            <SelectTrigger
                              className="h-11 sm:h-8 rounded-sm w-[96px] text-xs"
                              data-testid={`plaid-account-backfill-months-${a.account_id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 6, 12, 24].map((m) => (
                                <SelectItem key={m} value={String(m)}>{m} mo</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => backfillOneAccount(a.account_id)}
                            disabled={busy !== null || acctPaused || item.enabled === false}
                            className="rounded-sm h-11 sm:h-8 px-2 text-xs"
                            data-testid={`plaid-account-backfill-${a.account_id}`}
                            title="Pull older statement history directly by date, catching anything the regular sync missed"
                          >
                            {busy === `backfill-${a.account_id}` ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              "Pull months"
                            )}
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
                {(item.accounts || []).length === 0 && (
                  <p className="mt-2 ml-8 text-xs text-muted-foreground">No accounts detected on this connection yet — click the sync icon to refresh.</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
